/**
 * Strategy Constants
 * Adapted from promptfoo/redteam constants for EarlyCore demo flow
 */

import type { StrategyPreset, StrategyItem } from '@/types/redteam';

/**
 * Strategy type from promptfoo (subset of Strategy)
 */
export type StrategyId =
  // Recommended
  | 'basic'
  | 'jailbreak:composite'
  | 'jailbreak:meta'
  // Agentic Single-turn
  | 'jailbreak'
  | 'jailbreak:tree'
  // Agentic Multi-turn
  | 'crescendo'
  | 'goat'
  | 'jailbreak:hydra'
  // Multi-modal
  | 'audio'
  | 'image'
  | 'video';

/**
 * Strategy Preset Configuration
 * Type is defined in @/types/redteam.ts
 */

/**
 * Strategy Display Names
 * Maps strategy IDs to human-readable names
 */
export const STRATEGY_DISPLAY_NAMES: Record<StrategyId, string> = {
  basic: 'Basic',
  'jailbreak:composite': 'Composite Jailbreaks',
  'jailbreak:meta': 'Meta Agents',
  jailbreak: 'Single-shot Optimization',
  'jailbreak:tree': 'Tree-based Optimization',
  crescendo: 'Multi-turn Crescendo',
  goat: 'Generative Offensive Agent Tester',
  'jailbreak:hydra': 'Hydra Multi-Turn',
  audio: 'Audio',
  image: 'Image',
  video: 'Video',
};

/**
 * Strategy Descriptions
 * Maps strategy IDs to descriptions from promptfoo metadata
 */
export const STRATEGY_DESCRIPTIONS: Record<StrategyId, string> = {
  basic: 'Equivalent to no strategy. Always included. Can be disabled in configuration.',
  'jailbreak:composite':
    'Chains multiple attack vectors together for enhanced effectiveness. Combines different jailbreak techniques to create more sophisticated attacks that can bypass layered security controls.',
  'jailbreak:meta':
    'Agent that dynamically builds an attack taxonomy and learns from attack history. Uses meta-learning to adapt and improve attack strategies based on previous attempts and system responses.',
  jailbreak:
    'Optimizes single-turn attacks to bypass security controls. Uses advanced optimization techniques to craft the most effective single-shot attack prompts.',
  'jailbreak:tree':
    'Implements tree-based search for optimal attack paths. Explores multiple attack variations systematically to find the most successful approach using tree search algorithms.',
  crescendo:
    'Executes progressive multi-turn attacks with escalating malicious intent. Gradually increases the severity and complexity of attacks over multiple conversation turns to test system resilience.',
  goat: 'Deploys dynamic attack generation using advanced adversarial techniques. Uses generative models to create novel attack patterns that adapt in real-time to system defenses.',
  'jailbreak:hydra':
    'Multi-turn conversational attacks with meta-learning that adapts strategy based on full conversation history. Maintains context across multiple turns to craft increasingly effective attacks.',
  audio:
    'Tests detection and handling of audio-based malicious payloads. Validates how the system processes and responds to potentially harmful audio content.',
  image:
    'Tests detection and handling of image-based malicious payloads. Evaluates system resilience against attacks embedded in or using image data.',
  video:
    'Tests detection and handling of video-based malicious payloads. Assesses how the system handles potentially malicious video content and embedded attacks.',
};

/**
 * Strategy Item with metadata for UI display
 * Type is defined in @/types/redteam.ts
 */

/**
 * All strategies available in the demo flow
 */
export const STRATEGIES: StrategyItem[] = [
  // Recommended Strategies
  {
    id: 'basic',
    name: STRATEGY_DISPLAY_NAMES.basic,
    description: STRATEGY_DESCRIPTIONS.basic,
    category: 'recommended',
  },
  {
    id: 'jailbreak:composite',
    name: STRATEGY_DISPLAY_NAMES['jailbreak:composite'],
    description: STRATEGY_DESCRIPTIONS['jailbreak:composite'],
    category: 'recommended',
    locked: true,
    requiresPayment: true,
  },
  {
    id: 'jailbreak:meta',
    name: STRATEGY_DISPLAY_NAMES['jailbreak:meta'],
    description: STRATEGY_DESCRIPTIONS['jailbreak:meta'],
    category: 'recommended',
    locked: true,
    requiresPayment: true,
  },
  // Agentic Single-turn Only
  {
    id: 'jailbreak',
    name: STRATEGY_DISPLAY_NAMES.jailbreak,
    description: STRATEGY_DESCRIPTIONS.jailbreak,
    category: 'agentic-single',
    locked: true,
    requiresPayment: true,
  },
  {
    id: 'jailbreak:meta',
    name: STRATEGY_DISPLAY_NAMES['jailbreak:meta'],
    description: STRATEGY_DESCRIPTIONS['jailbreak:meta'],
    category: 'agentic-single',
    locked: true,
    requiresPayment: true,
  },
  {
    id: 'jailbreak:tree',
    name: STRATEGY_DISPLAY_NAMES['jailbreak:tree'],
    description: STRATEGY_DESCRIPTIONS['jailbreak:tree'],
    category: 'agentic-single',
    locked: true,
    requiresPayment: true,
  },
  // Agentic Single and Multi-turn
  {
    id: 'crescendo',
    name: STRATEGY_DISPLAY_NAMES.crescendo,
    description: STRATEGY_DESCRIPTIONS.crescendo,
    category: 'agentic-multi',
    locked: true,
    requiresPayment: true,
  },
  {
    id: 'goat',
    name: STRATEGY_DISPLAY_NAMES.goat,
    description: STRATEGY_DESCRIPTIONS.goat,
    category: 'agentic-multi',
    locked: true,
    requiresPayment: true,
  },
  {
    id: 'jailbreak:hydra',
    name: STRATEGY_DISPLAY_NAMES['jailbreak:hydra'],
    description: STRATEGY_DESCRIPTIONS['jailbreak:hydra'],
    category: 'agentic-multi',
    locked: true,
    requiresPayment: true,
  },
  // Multi-modal Strategies
  {
    id: 'audio',
    name: STRATEGY_DISPLAY_NAMES.audio,
    description: STRATEGY_DESCRIPTIONS.audio,
    category: 'multimodal',
    locked: true,
    requiresPayment: true,
  },
  {
    id: 'image',
    name: STRATEGY_DISPLAY_NAMES.image,
    description: STRATEGY_DESCRIPTIONS.image,
    category: 'multimodal',
  },
  {
    id: 'video',
    name: STRATEGY_DISPLAY_NAMES.video,
    description: STRATEGY_DESCRIPTIONS.video,
    category: 'multimodal',
    locked: true,
    requiresPayment: true,
  },
];

/**
 * Strategy presets available in the demo flow
 */
export const STRATEGY_PRESETS: StrategyPreset[] = [
  {
    id: 'quick',
    name: 'Quick',
    description: 'Use to verify that your configuration is correct.',
    available: true,
    strategyIds: {
      recommended: ['basic'],
    },
  },
  {
    id: 'medium',
    name: 'Medium',
    description: 'Recommended strategies for moderate coverage',
    available: false,
    requiresPayment: true,
    strategyIds: {
      recommended: ['jailbreak:composite'],
      agenticSingleTurn: ['jailbreak', 'jailbreak:meta'],
      agenticMultiTurn: ['crescendo', 'jailbreak:hydra'],
    },
  },
  {
    id: 'large',
    name: 'Large',
    description: 'A larger set of strategies for a more comprehensive redteam.',
    available: false,
    requiresPayment: true,
    strategyIds: {
      recommended: ['jailbreak:composite', 'jailbreak:meta'],
      agenticSingleTurn: ['jailbreak', 'jailbreak:meta', 'jailbreak:tree'],
      agenticMultiTurn: ['crescendo', 'goat', 'jailbreak:hydra'],
    },
  },
];

/**
 * Get display name for a strategy
 */
export function getStrategyDisplayName(strategyId: StrategyId): string {
  return STRATEGY_DISPLAY_NAMES[strategyId] || strategyId;
}

/**
 * Get description for a strategy
 */
export function getStrategyDescription(strategyId: StrategyId): string {
  return STRATEGY_DESCRIPTIONS[strategyId] || `Strategy: ${strategyId}`;
}
